import type { DemoDayAvailabilityDto } from '@fluenceur/types';

/** How many slots the invitation puts forward before "other slots". */
const FEATURED_SLOT_COUNT = 3;

/**
 * The slots put forward first: the earliest of each day that has one, so the
 * three choices spread over three days rather than three hours of one morning.
 * Fewer than three days open, the remaining places go to the earliest slots left.
 */
export function featuredSlots(days: DemoDayAvailabilityDto[]): string[] {
  const picked = days.flatMap((day) => day.slots.slice(0, 1)).slice(0, FEATURED_SLOT_COUNT);
  if (picked.length < FEATURED_SLOT_COUNT) {
    const rest = days
      .flatMap((day) => day.slots)
      .filter((slot) => !picked.includes(slot))
      .sort();
    picked.push(...rest.slice(0, FEATURED_SLOT_COUNT - picked.length));
  }
  return picked.sort();
}

export interface SlotLabels {
  /** "Mer. 22" */
  day: string;
  /** "10:30" */
  time: string;
  /** "Mercredi 22 octobre · 10:30", to open a sentence */
  full: string;
}

const FORMATS = {
  weekday: { weekday: 'short' },
  dayOfMonth: { day: 'numeric' },
  time: { hour: '2-digit', minute: '2-digit' },
  date: { weekday: 'long', day: 'numeric', month: 'long' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

/** A slot list renders dozens of labels per click: build each formatter once. */
const formatters = new Map<string, Intl.DateTimeFormat>();
function format(kind: keyof typeof FORMATS, locale: string, timeZone: string, date: Date) {
  const key = `${kind}|${locale}|${timeZone}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { ...FORMATS[kind], timeZone });
    formatters.set(key, formatter);
  }
  return formatter.format(date);
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** "Mercredi 1er octobre": French writes the first of a month "1er", which `Intl` does not. */
function longDate(date: Date, locale: string, timeZone: string) {
  const written = capitalize(format('date', locale, timeZone, date));
  return locale.startsWith('fr') ? written.replace(/ 1 /, ' 1er ') : written;
}

export function slotLabels(start: string, locale: string, timeZone: string): SlotLabels {
  const date = new Date(start);
  const time = format('time', locale, timeZone, date);
  return {
    day: `${capitalize(format('weekday', locale, timeZone, date))} ${format('dayOfMonth', locale, timeZone, date)}`,
    time,
    full: `${longDate(date, locale, timeZone)} · ${time}`,
  };
}

/** "Mercredi 22 octobre" for a `YYYY-MM-DD` day heading. */
export function dayHeading(date: string, locale: string): string {
  return longDate(new Date(`${date}T12:00:00Z`), locale, 'UTC');
}
